import { Injectable, signal } from '@angular/core';

// Definimos los idiomas compatibles
export type AppLanguage = 'en' | 'es' | 'nl' | 'fr' | 'de' | 'it';

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    // Estado reactivo del idioma
    private readonly _currentLang = signal<AppLanguage>('en');
    readonly currentLang = this._currentLang.asReadonly();

    /**
     * Diccionario Global de UI - Refactorizado con Namespacing
     * Estructura:
     * - common: Reutilizable (Botones, Errores, Macros)
     * - viewer: Específico para e-menu-viewer (Cliente final)
     * - admin: Específico para admin-panel (Gestión)
     */
    readonly ui = {
        // ============================================================
        // 🟢 COMMON (Compartido entre ambas apps)
        // ============================================================
        common: {
            actions: {
                save: { en: 'Save', es: 'Guardar', nl: 'Opslaan', fr: 'Sauvegarder', de: 'Speichern', it: 'Salva' },
                cancel: { en: 'Cancel', es: 'Cancelar', nl: 'Annuleren', fr: 'Annuler', de: 'Abbrechen', it: 'Annulla' },
                delete: { en: 'Delete', es: 'Eliminar', nl: 'Verwijderen', fr: 'Supprimer', de: 'Löschen', it: 'Elimina' },
                edit: { en: 'Edit', es: 'Editar', nl: 'Bewerken', fr: 'Modifier', de: 'Bearbeiten', it: 'Modifica' },
                retry: { en: 'Retry', es: 'Reintentar', nl: 'Opnieuw', fr: 'Réessayer', de: 'Wiederholen', it: 'Riprova' },
                loading: { en: 'Loading...', es: 'Cargando...', nl: 'Laden...', fr: 'Chargement...', de: 'Laden...', it: 'Caricamento...' }
            },
            errors: {
                title: { en: 'Oops! Something went wrong', es: '¡Vaya! Algo ha fallado', nl: 'Oeps! Er is iets misgegaan', fr: 'Oups! Un problème est survenu', de: 'Hoppla! Etwas ist schief gelaufen', it: 'Ops! Qualcosa es andato storto' },
                generic: { en: 'Please try again later.', es: 'Por favor, inténtalo de nuevo.', nl: 'Probeer het later opnieuw.', fr: 'Veuillez réessayer plus tard.', de: 'Bitte versuchen Sie es später erneut.', it: 'Riprova più tardi.' }
            },
            macros: {
                calories: { en: 'Kcal', es: 'Kcal', nl: 'Kcal', fr: 'Kcal', de: 'Kcal', it: 'Kcal' },
                proteins: { en: 'Protein', es: 'Prot', nl: 'Eiwit', fr: 'Prot', de: 'Eiweiß', it: 'Prot' },
                carbs: { en: 'Carbs', es: 'Carbos', nl: 'Koolh', fr: 'Gluc', de: 'Kohlenh', it: 'Carbo' },
                fats: { en: 'Fats', es: 'Grasas', nl: 'Vetten', fr: 'Graisses', de: 'Fette', it: 'Grassi' }
            },
            nav: {
                all: { en: 'All', es: 'Todos', nl: 'Alle', fr: 'Tout', de: 'Alle', it: 'Tutti' }
            }
        },

        // ============================================================
        // 🔵 VIEWER (Solo para e-menu-viewer)
        // ============================================================
        viewer: {
            detailsBtn: {
                en: 'Details & Nutrition',
                es: 'Ver detalles y Nutrición',
                nl: 'Details & Voeding',
                fr: 'Détails & Nutrition',
                de: 'Details & Ernährung',
                it: 'Dettagli e Nutrizione'
            },
            filters: {
                vegan: { en: 'Vegan', es: 'Vegano', nl: 'Veganistisch', fr: 'Végétalien', de: 'Vegan', it: 'Vegano' },
                glutenFree: { en: 'Gluten Free', es: 'Sin Gluten', nl: 'Glutenvrij', fr: 'Sans Gluten', de: 'Glutenfrei', it: 'Senza Glutine' },
                vegetarian: { en: 'Vegetarian', es: 'Vegetariano', nl: 'Vegetarisch', fr: 'Végétarien', de: 'Vegetarisch', it: 'Vegetariano' }
            },
            ecoStatus: {
                high: {
                    en: 'High carbon footprint. Conscious choice recommended.',
                    es: 'Este plato tiene una huella alta. Consumo responsable.',
                    nl: 'Hoge CO2-voetafdruk. Bewuste keuze aanbevolen.',
                    fr: 'Empreinte carbone élevée. Choix conscient recommandé.',
                    de: 'Hoher CO2-Fußabdruck. Bewusste Entscheidung empfohlen.',
                    it: 'Elevata impronta di carbonio. Si raccomanda una elección consciente.'
                },
                moderate: {
                    en: 'Moderate footprint for this protein type.',
                    es: 'Huella moderada para este tipo de proteínas.',
                    nl: 'Gemiddelde voetafdruk voor dit type proteïne.',
                    fr: 'Empreinte modérée pour ce type de protéine.',
                    de: 'Moderater Fußabdruck für diese Proteinart.',
                    it: 'Impronta moderata per questo tipo di proteine.'
                },
                low: {
                    en: 'Excellent sustainable choice!',
                    es: '¡Excelente elección sostenible!',
                    nl: 'Uitstekende duurzame keuze!',
                    fr: 'Excellent choix durable !',
                    de: 'Hervorragende nachhaltige Wahl!',
                    it: 'Eccellente scelta sostenibile!'
                }
            },
            labels: {
                allergens: { en: 'Allergens', es: 'Alérgenos', nl: 'Allergenen', fr: 'Allergènes', de: 'Allergene', it: 'Allergeni' },
                eco: { en: 'Environmental Impact', es: 'Impacto Ambiental', nl: 'Milieu-impact', fr: 'Impact Environnemental', de: 'Umweltbelastung', it: 'Impatto Ambientale' },
                details: { en: 'Details', es: 'Detalles', nl: 'Details', fr: 'Détails', de: 'Details', it: 'Dettagli' },
                ingredients: { en: 'Ingredients', es: 'Ingredientes', nl: 'Ingrediënten', fr: 'Ingrédients', de: 'Zutaten', it: 'Ingredienti' },
                nutrition: {
                    en: 'Nutritional Balance',
                    es: 'Balance Nutricional',
                    nl: 'Voedingsbalans',
                    fr: 'Équilibre Nutritionnel',
                    de: 'Nährwertbilanz',
                    it: 'Equilibrio Nutrizionale'
                },
            },
            verificationLock: {
                title: {
                    en: 'Venue under review',
                    es: 'Local en revisión',
                    fr: 'Local en cours de révision',
                    de: 'Standort wird überprüft',
                    it: 'Locale in revisione',
                    nl: 'Locatie wordt beoordeeld'
                },
                subtitle: {
                    en: 'The establishment is ready to work but quality approval is pending.',
                    es: 'El local quiere trabajar pero falta el OK de calidad.',
                    fr: 'L’établissement est prêt à travailler mais l’approbation qualité est en attente.',
                    de: 'Der Standort ist bereit zu arbeiten, die Qualitätsprüfung steht jedoch noch aus.',
                    it: 'Questo menu è in fase di verifica dal nostro team di qualità. Sarà disponibile entro 24 ore.',
                    nl: 'Dit menu wordt gecontroleerd door ons kwaliteitsteam. Het is binnen 24 uur beschikbaar.'
                },
                suspendedTitle: {
                    en: 'Service paused',
                    es: 'Servicio pausado',
                    nl: 'Service gepauzeerd',
                    fr: 'Service suspendu',
                    de: 'Dienst pausiert',
                    it: 'Servizio in pausa'
                },
                suspendedSubtitle: {
                    en: 'The venue is disabled or temporarily suspended.',
                    es: 'El local está inhabilitado o suspendido temporalmente.',
                    nl: 'De locatie is uitgeschakeld of tijdelijk opgeschort.',
                    fr: 'L’établissement est désactivé o suspendu temporairement.',
                    de: 'Der Standort ist deaktiviert oder vorübergehend gesperrt.',
                    it: 'Il locale è disabilitato o sospeso temporaneamente.'
                }
            }
        },

        // ============================================================
        // 🔴 ADMIN (Solo para admin-panel)
        // ============================================================
        admin: {
            auth: {
                welcomeBack: { en: 'Welcome back', es: 'Bienvenido de nuevo', nl: 'Welkom terug', fr: 'Bon retour', de: 'Willkommen zurück', it: 'Bentornato' },
                createAccount: { en: 'Create your account', es: 'Crea tu cuenta', nl: 'Maak een account', fr: 'Créer un compte', de: 'Konto erstellen', it: 'Crea account' },
                emailLabel: { en: 'Professional Email', es: 'Email Profesional', nl: 'Zakelijk e-mailadres', fr: 'Email professionnel', de: 'Geschäftliche E-Mail', it: 'Email professionale' },
                passwordLabel: { en: 'Password', es: 'Contraseña', nl: 'Wachtwoord', fr: 'Mot de passe', de: 'Passwort', it: 'Password' },
                loginBtn: { en: 'Login to Panel', es: 'Entrar al Panel', nl: 'Inloggen', fr: 'Connexion', de: 'Anmelden', it: 'Accedi' },
                registerBtn: { en: 'Register Restaurant', es: 'Registrar Restaurante', nl: 'Restaurant registreren', fr: 'Enregistrer le restaurant', de: 'Restaurant registrieren', it: 'Registra ristorante' },
                subtitle: { en: 'Digitize your restaurant with AI', es: 'Digitaliza tu restaurante con IA', nl: 'Digitaliseer uw restaurant met AI', fr: 'Digitalisez votre restaurant avec l\'IA', de: 'Digitalisieren Sie Ihr Restaurant mit KI', it: 'Digitalizza il tuo restaurante con l\'IA' },
                toggleLogin: { en: 'Already have an account?', es: '¿Ya tienes cuenta?', nl: 'Heeft u al een account?', fr: 'Vous avez déjà un compte ?', de: 'Haben Sie bereits ein Konto?', it: 'Hai già un account?' },
                toggleRegister: { en: 'New here?', es: '¿Nuevo hostelero?', nl: 'Nieuw hier?', fr: 'Nouveau ici ?', de: 'Neu hier?', it: 'Nuovo qui?' },

                // Recuperación de Contraseña
                forgotPasswordTitle: { es: 'Recuperar Clave', en: 'Recover Password', nl: 'Wachtwoord vergeten', fr: 'Récupérer le mot de passe', de: 'Passwort wiederherstellen', it: 'Recupera password' },
                newPasswordTitle: { es: 'Nueva Contraseña', en: 'New Password', nl: 'Nieuw Wachtwoord', fr: 'Nouveau Mot de Passe', de: 'Neues Passwort', it: 'Nuova password' },
                forgotSubtitle: {
                    es: 'Introduce tu email para recibir instrucciones',
                    en: 'Enter your email to receive instructions',
                    nl: 'Voer uw e-mailadres in om instructies te ontvangen',
                    fr: 'Entrez votre e-mail pour recevoir des instructions',
                    de: 'Geben Sie Ihre E-Mail-Adresse ein, um Anweisungen zu erhalten',
                    it: 'Inserisci la tua email per ricevere istruzioni'
                },
                updateSubtitle: {
                    es: 'Establece tu nueva clave de acceso',
                    en: 'Set your new access password',
                    nl: 'Stel uw nieuwe accesoires en wachtwoord in',
                    fr: 'Définissez votre nouveau mot de passe d\'accès',
                    de: 'Legen Sie Ihr neues Passwort fest',
                    it: 'Imposta la tua nuova password di accesso'
                },
                sendInstructionsBtn: {
                    es: 'Enviar Instrucciones',
                    en: 'Send Instructions',
                    nl: 'Instructies verzenden',
                    fr: 'Envoyer les instructions',
                    de: 'Anweisungen senden',
                    it: 'Invia istruzioni'
                },
                updatePasswordBtn: {
                    es: 'Actualizar Contraseña',
                    en: 'Update Password',
                    nl: 'Wachtwoord bijwerken',
                    fr: 'Mettre à jour le mot de passe',
                    de: 'Passwort aktualisieren',
                    it: 'Aggiorna password'
                },
                backToLogin: {
                    es: 'Volver al inicio',
                    en: 'Back to login',
                    nl: 'Terug naar inloggen',
                    fr: 'Retour à la connexion',
                    de: 'Zurück zum Login',
                    it: 'Torna al login'
                },
                forgotLink: {
                    es: '¿Olvidaste tu contraseña?',
                    en: 'Forgot your password?',
                    nl: 'Wachtwoord vergeten?',
                    fr: 'Mot de passe oublié ?',
                    de: 'Passwort vergessen?',
                    it: 'Password dimenticata?'
                },

                forgotPasswordSuccess: {
                    es: 'Te hemos enviado un correo con las instrucciones.',
                    en: 'We have sent you an email with instructions.',
                    nl: 'We hebben u een e-mail gestuurd met instructies.',
                    fr: 'Nous vous avons envoyé un e-mail con instructions.',
                    de: 'Wir haben Ihnen eine E-Mail mit Anweisungen gesendet.',
                    it: 'Ti abbiamo inviato un\'email con le istruzioni.'
                },
                rateLimitError: {
                    es: 'Límite excedido: Solo se permiten 2 correos de recuperación por hora.',
                    en: 'Rate limit exceeded: Only 2 recovery emails per hour are allowed.',
                    nl: 'Limiet overschreden: Er zijn slechts 2 herstel-e-mails per uur toegestaan.',
                    fr: 'Limite dépassée : Seuls 2 e-mails de récupération par heure sont autorisés.',
                    de: 'Limit überschritten: Es sind nur 2 Wiederherstellungs-E-Mails pro Stunde erlaubt.',
                    it: 'Limite superato: sono consentite solo 2 email di recupero all\'ora.'
                }
            },
            nav: {
                dashboard: { en: 'Dashboard', es: 'Gestión', nl: 'Dashboard', fr: 'Tableau de bord', de: 'Dashboard', it: 'Pannello' },
                myMenu: { en: 'My Menu', es: 'Mi Carta', nl: 'Mijn Menu', fr: 'Mon Menu', de: 'Mein Menü', it: 'Il Mio Menu' },
                profile: { en: 'Profile', es: 'Perfil', nl: 'Profiel', fr: 'Profil', de: 'Profil', it: 'Profilo' },
                logout: { en: 'Logout', es: 'Cerrar Sesión', nl: 'Uitloggen', fr: 'Déconnexion', de: 'Abmelden', it: 'Disconnettersi' }
            },
            dashboard: {
                title: { en: 'Network Management', es: 'Gestión de Red', nl: 'Netwerkbeheer', fr: 'Gestion du réseau', de: 'Netzwerkverwaltung', it: 'Gestione della rete' },
                totalDishes: { en: 'Total Dishes', es: 'Platos Totales', nl: 'Totaal Gerechten', fr: 'Plats totaux', de: 'Gesamtgerichte', it: 'Piatti totali' },
                activeVenues: { en: 'Active Venues', es: 'Locales Activos', nl: 'Actieve locaties', fr: 'Lieux actifs', de: 'Aktive Standorte', it: 'Locali attivi' },
                venueSelector: { en: 'Switch Venue', es: 'Cambiar Local', nl: 'Locatie wijzigen', fr: 'Changer de lieu', de: 'Standort wechseln', it: 'Cambia locale' },
                searchPlaceholder: { en: 'Search by name or slug...', es: 'Buscar por nombre o slug...', nl: 'Zoeken op naam o slug...', fr: 'Rechercher par nom ou slug...', de: 'Suche nach Name oder Slug...', it: 'Cerca per nome o slug...' },
                status: {
                    active: { en: 'Active', es: 'Activos', nl: 'Actief', fr: 'Actif', de: 'Aktiv', it: 'Attivo' },
                    inactive: { en: 'Off / Paused', es: 'Off / Pausados', nl: 'Gepauzeerd', fr: 'En pause', de: 'Pausiert', it: 'In pausa' },
                    all: { en: 'All', es: 'Todos', nl: 'Alle', fr: 'Tout', de: 'Alle', it: 'Tutti' }
                },
                badges: {
                    public: { en: 'Public & Active Menu', es: 'Menú Público y Activo', nl: 'Openbaar & Actief Menu', fr: 'Menu public et actif', de: 'Öffentliches & Aktives Menü', it: 'Menu pubblico e attivo' },
                    paused: { en: 'Menu Paused / Hidden', es: 'Menú Pausado / Oculto', nl: 'Menu gepauzeerd / verborgen', fr: 'Menu en pause / masqué', de: 'Menü pausiert / ausgeblendet', it: 'Menu in pausa / nascosto' },
                    review: { en: 'Under quality review', es: 'En proceso de revisión', nl: 'In kwaliteitscontrole', fr: 'En cours de révision', de: 'In Qualitätsprüfung', it: 'In fase di revisione' }
                },
                auth: {
                    globalAdmin: { en: 'Global Administration', es: 'Administración Global', nl: 'Globaal Beheer', fr: 'Administration globale', de: 'Globale Verwaltung', it: 'Amministrazione globale' },
                    businessSummary: { en: 'Business Summary', es: 'Resumen de Negocio', nl: 'Zakelijk Overzicht', fr: 'Résumé des affaires', de: 'Geschäftsübersicht', it: 'Riepilogo aziendale' }
                },
                verification: {
                    pendingTitle: { en: 'Service pending verification', es: 'Servicio pendiente de verificación', nl: 'Dienst in afwachting van verificatie', fr: 'Service en attente de vérificación', de: 'Dienst wartet auf Verifizierung', it: 'Servizio in attesa di verifica' },
                    pendingDetail: {
                        en: 'Your menu "{{name}}" is being reviewed by our quality team. This process can take up to 24h.',
                        es: 'Tu menú "{{name}}" está siendo revisado por nuestro equipo de calidad. Este proceso puede tardar hasta 24h.',
                        nl: 'Uw menu "{{name}}" wordt beoordeeld door ons kwaliteitsteam. Dit proces kan tot 24 uur duren.',
                        fr: 'Votre menu "{{name}}" est en cours de révision par notre équipe de qualité. Ce processus peut prendre hasta 24h.',
                        de: 'Ihr Menü "{{name}}" wird von unserem Qualitätsteam überprüft. Dieser Vorgang kann bis zu 24 Stunden dauern.',
                        it: 'Il tuo menu "{{name}}" è in fase di revisione dal nostro team di qualità. Questo processo può richiedere fino a 24 ore.'
                    }
                },
                list: {
                    newRegistration: { en: 'New Registration', es: 'Nuevo registro', nl: 'Nieuwe registratie', fr: 'Nouvel enregistrement', de: 'Neu registriert', it: 'Nuova registrazione' },
                    underReview: { en: 'Review', es: 'Revisión', nl: 'Beoordeling', fr: 'Révision', de: 'Prüfung', it: 'Revisione' },
                    usageLabel: { en: 'usage', es: 'de uso', nl: 'verbruik', fr: 'd\'utilisation', de: 'Nutzung', it: 'di utilizzo' },
                    tenure: { en: 'Tenure', es: 'Antigüedad', nl: 'Anciënniteit', fr: 'Ancienneté', de: 'Zugehörigkeit', it: 'Anzianità' },
                    tenureValues: {
                        new: { es: 'NUEVO', en: 'NEW', nl: 'NIEUW', fr: 'NOUVEAU', de: 'NEU', it: 'NUOVO' },
                        thisMonth: { es: 'ESTE MES', en: 'THIS MONTH', nl: 'DEZE MAAND', fr: 'CE MOIS', de: 'DIESER MONAT', it: 'QUESTO MESE' },
                        month: { es: '1 MES', en: '1 MONTH', nl: '1 MAAND', fr: '1 MOIS', de: '1 MONAT', it: '1 MESE' },
                        months: { es: '{{count}} MESES', en: '{{count}} MONTHS', nl: '{{count}} MAANDEN', fr: '{{count}} MOIS', de: '{{count}} MONATE', it: '{{count}} MESI' }
                    },
                    plans: {
                        basico: { es: 'BÁSICO', en: 'BASIC', nl: 'BASIS', fr: 'BASIQUE', de: 'BASIS', it: 'BASE' },
                        pro: { es: 'PRO', en: 'PRO', nl: 'PRO', fr: 'PRO', de: 'PRO', it: 'PRO' },
                        gourmet: { es: 'GOURMET', en: 'GOURMET', nl: 'GOURMET', fr: 'GOURMET', de: 'GOURMET', it: 'GOURMET' }
                    },
                    published: { en: 'Published', es: 'Publicado', nl: 'Gepubliceerd', fr: 'Publié', de: 'Veröffentlicht', it: 'Pubblicato' },
                    yes: { en: 'YES', es: 'SÍ', nl: 'JA', fr: 'OUI', de: 'JA', it: 'SÌ' },
                    no: { en: 'NO', es: 'NO', nl: 'NEE', fr: 'NON', de: 'NEIN', it: 'NO' }
                },
                actions: {
                    approve: { en: 'Approve & Publish', es: 'Aprobar y Publicar', nl: 'Goedkeuren & Publiceren', fr: 'Approuver et publier', de: 'Genehmigen & Veröffentlichen', it: 'Approva e pubblica' },
                    suspend: { en: 'Suspend', es: 'Suspender', nl: 'Schorsen', fr: 'Suspendre', de: 'Suspendieren', it: 'Sospendere' },
                    activate: { en: 'Activate', es: 'Activar', nl: 'Activeren', fr: 'Activer', de: 'Aktivieren', it: 'Attivare' }
                },
                empty: {
                    noResults: { en: 'No results', es: 'Sin resultados', nl: 'Geen resultaten', fr: 'Aucun résultat', de: 'Keine Ergebnisse', it: 'Nessun risultato' },
                    clearFilters: { en: 'Clear filters', es: 'Limpiar filtros', nl: 'Filters wissen', fr: 'Effacer les filtres', de: 'Filter löschen', it: 'Cancella filtri' }
                }
            },
            menu: {
                title: { en: 'My Menu', es: 'Mi Carta', nl: 'Mijn Menu', fr: 'Mon Menu', de: 'Mein Menü', it: 'Il Mio Menu' },
                registeredDishes: { en: 'Registered Dishes', es: 'Platos registrados', nl: 'Geregistreerde gerechten', fr: 'Plats enregistrés', de: 'Registrierte Gerichte', it: 'Piatti registrati' },
                manualAdd: { en: 'Manual Add', es: 'Añadir Manual', nl: 'Handmatig toevoegen', fr: 'Ajout manuel', de: 'Manuell hinzufügen', it: 'Aggiunta manuale' },
                aiScan: { en: 'AI Scan', es: 'Scan IA', nl: 'AI Scan', fr: 'Scan IA', de: 'KI-Scan', it: 'Scansione IA' },
                scanning: { en: 'Analyzing...', es: 'Analizando...', nl: 'Analyseren...', fr: 'Analyse...', de: 'Analysieren...', it: 'Analizzando...' },
                emptyState: {
                    title: { en: 'Your menu <span class="text-emerald-500 italic">in seconds</span>', es: 'Tu menú <span class="text-emerald-500 italic">en segundos</span>', nl: 'Uw menu <span class="text-emerald-500 italic">in seconden</span>', fr: 'Votre menu <span class="text-emerald-500 italic">en secondes</span>', de: 'Ihr Menü <span class="text-emerald-500 italic">in Sekunden</span>', it: 'Il tuo menu <span class="text-emerald-500 italic">in secondi</span>' },
                    subtitle: { en: 'Scan your physical menu and let our AI do all the work', es: 'Escanea tu carta física y deja que nuestra IA haga todo el trabajo', nl: 'Scan uw fysieke menu en laat onze AI al het werk doen', fr: 'Scannez votre menu physique et laissez notre IA faire tout le travail', de: 'Scannen Sie Ihr physisches Menü und lassen Sie unsere KI die ganze Arbeit erledigen', it: 'Scansiona il tuo menu fisico e lascia que la nostra IA faccia tutto il lavoro' }
                }
            },
            editor: {
                dishDetails: { en: 'Dish Details', es: 'Detalles del Plato', nl: 'Gerecht Details', fr: 'Détails du plat', de: 'Gerichtsdetails', it: 'Dettagli del piatto' },
                namePlaceholder: { en: 'Dish Name', es: 'Nombre del Plato', nl: 'Gerechtnaam', fr: 'Nom du plat', de: 'Gerichtsname', it: 'Nome del piatto' },
                descPlaceholder: { en: 'Marketing Description', es: 'Descripción Marketing', nl: 'Marketingbeschrijving', fr: 'Description marketing', de: 'Marketingbeschreibung', it: 'Descrizione marketing' },
                price: { en: 'Price', es: 'Precio', nl: 'Prijs', fr: 'Prix', de: 'Preis', it: 'Prezzo' },
                category: { en: 'Category', es: 'Categoría', nl: 'Categorie', fr: 'Catégorie', de: 'Kategorie', it: 'Categoria' },
                publish: { en: 'Publish Changes', es: 'Publicar Cambios', nl: 'Wijzigingen publiceren', fr: 'Publier les modifications', de: 'Änderungen veröffentlichen', it: 'Pubblica modifiche' }
            },
            setup: {
                welcome: {
                    title: { en: 'Start your <br><span style="color: #10b981; font-style: italic;">Digital journey</span>', es: 'Comience su viaje <br><span style="color: #10b981; font-style: italic;">Digital e Inteligente</span>', nl: 'Begin uw <br><span style="color: #10b981; font-style: italic;">digitale reis</span>', fr: 'Commencez votre <br><span style="color: #10b981; font-style: italic;">voyage numérique</span>', de: 'Beginnen Sie Ihre <br><span style="color: #10b981; font-style: italic;">digitale Reise</span>', it: 'Inizia il tuo <br><span style="color: #10b981; font-style: italic;">viaggio digitale</span>' },
                    subtitle: { en: 'Set up your venue in less than 1 minute.', es: 'Configure su local en menos de 1 minuto.', nl: 'Stel uw locatie in minder dan 1 minuut in.', fr: 'Configurez votre lieu en moins d\'une minute.', de: 'Richten Sie Ihren Standort in weniger als 1 Minute ein.', it: 'Configura il tuo locale in meno di 1 minuto.' }
                },
                commercialName: { en: 'Commercial Name', es: 'Nombre Comercial', nl: 'Handelsnaam', fr: 'Nom commercial', de: 'Handelsname', it: 'Nome commerciale' },
                slugLabel: { en: 'Web Address (Slug)', es: 'Dirección Web (Slug)', nl: 'Webadres (Slug)', fr: 'Adresse Web (Slug)', de: 'Webadresse (Slug)', it: 'Indirizzo Web (Slug)' },
                activateBtn: { en: 'Activate My Menu', es: 'Activar Mi Menú', nl: 'Activeer Mijn Menu', fr: 'Activer mon menu', de: 'Mein Menü aktivieren', it: 'Attiva il mio menu' }
            }
        }
    };

    // Configuración de banderas para el selector UI
    readonly languages = [
        { code: 'en' as AppLanguage, flagUrl: 'https://flagcdn.com/w40/gb.png', label: 'English' },
        { code: 'es' as AppLanguage, flagUrl: 'https://flagcdn.com/w40/es.png', label: 'Español' },
        { code: 'nl' as AppLanguage, flagUrl: 'https://flagcdn.com/w40/nl.png', label: 'Nederlands' },
        { code: 'fr' as AppLanguage, flagUrl: 'https://flagcdn.com/w40/fr.png', label: 'Français' },
        { code: 'de' as AppLanguage, flagUrl: 'https://flagcdn.com/w40/de.png', label: 'Deutsch' },
        { code: 'it' as AppLanguage, flagUrl: 'https://flagcdn.com/w40/it.png', label: 'Italiano' }
    ];

    constructor() {
        // Carga de persistencia local
        if (typeof localStorage !== 'undefined') {
            const saved = localStorage.getItem('app_lang') as AppLanguage;
            if (saved && ['en', 'es', 'nl', 'fr', 'de', 'it'].includes(saved)) {
                this._currentLang.set(saved);
            }
        }
    }

    setLanguage(lang: AppLanguage, vibrate = true) {
        this._currentLang.set(lang);
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('app_lang', lang);
        }

        // Feedback háptico opcional para móviles
        if (vibrate && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
            try {
                navigator.vibrate(10);
            } catch (e) {
                // Silenciamos errores de intervención del navegador
            }
        }
    }
}